import React from 'react';
import { useRef } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { createUseStyles } from 'react-jss';
import cards from '../../config/inspirationCards';
import variables from '../../theme/variables';
import InspirationCard from './InspirationCard';
import throttle from 'lodash.throttle';

const useStyles = createUseStyles(
    {
        wrap: {
            position: 'relative',
            width: '100%',
            // TODO check height calculations
            '@media (min-width: 1024px)': {
                height: `calc(var(--app-height) - ${variables.msftHeaderHeight}px)`,
                overflow: 'hidden',
                background: variables.palette.black,
                marginTop: variables.msftHeaderHeight,
            },
        },

        outer: {
            width: 2200,
            height: 1200,
            position: 'absolute',
            left: '50%',
            top: '50%',
            transition: 'transform 0.6s ease-out',
        },

        inner: {
            width: '2000px',
            height: '1000px',
            position: 'absolute',
            top: 100,
            left: 100,
        },

        bg: {
            width: '2000px',
            height: '1000px',
            position: 'absolute',
            left: '50%',
            top: '50%',
            transition: 'transform 0.6s ease-out',
            background: `url(${window.rootUrl}/img/boardBg.webp)`,
        },

        backdrop: {
            background: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            top: '-var(--app-height)',
            left: '-100vw',
            width: '300vw',
            height: '300vh',
            zIndex: 50,
        },

        mobileInner: {
            position: 'relative',
            marginTop: 200,
        },
        cardWrapMobile: {
            position: 'relative',
            marginBottom: 20,
        },
    },
    { name: 'InspirationBoard' }
);

export default function InspirationBoard() {
    const classes = useStyles();
    const boardRef = useRef();

    const [layout, setLayout] = useState('desktop');

    const [selected, setSelected] = useState(null); // null | card
    const [translateX, setTranslateX] = useState(0);
    const [translateY, setTranslateY] = useState(0);

    useEffect(() => {
        if (!selected) {
            setTranslateX(0);
            setTranslateY(0);
            return;
        }

        const rect = boardRef.current.getBoundingClientRect();
        const boardH = rect.height;
        const boardW = rect.width;

        const cardT = selected.position[layout].top;
        const cardL = selected.position[layout].left;
        const cardH = selected.position[layout].height;
        const cardW = selected.position[layout].width;

        const cardB = boardH - cardT - cardH;
        const cardR = boardW - cardL - cardW;

        setTranslateX((cardR - cardL) / 2);
        setTranslateY((cardB - cardT) / 2);
    }, [selected]);

    useEffect(() => {
        function checkLayout() {
            if (window.innerWidth < 1024) {
                setLayout('mobile');
            } else {
                setLayout('desktop');
            }
        }

        window.addEventListener('resize', checkLayout);
        checkLayout();

        return () => {
            window.removeEventListener('resize', checkLayout);
        };
    }, []);

    const handleClose = () => {
        setSelected(null);
    };

    const handleOpenCard = (card) => {
        setSelected(card);
    };

    const handleMouseMove = (e) => {
        if (selected) return;
        if (window.innerWidth < 1024) return;

        const diffX = boardRef.current.clientWidth - window.innerWidth;
        const diffY = boardRef.current.clientHeight - window.innerHeight;
        const percX = e.clientX / window.innerWidth - 0.5; // subtract 0.5 to allow for negative values (ie - move left)
        const percY = e.clientY / window.innerHeight - 0.5;

        setTranslateX(-diffX * percX);
        setTranslateY(-diffY * percY);
    };

    return (
        <div
            className={classes.wrap}
            onMouseMove={throttle(handleMouseMove, 100)}
        >
            {layout === 'desktop' && (
                <>
                    <div
                        className={classes.bg}
                        style={{
                            transform: `translate(calc(-50% + ${
                                translateX / 2.5
                            }px), calc(-50% + ${translateY / 2.5}px))`,
                        }}
                    ></div>
                    <div
                        className={classes.outer}
                        style={{
                            transform: `translate(calc(-50% + ${translateX}px), calc(-50% + ${translateY}px))`,
                        }}
                        ref={boardRef}
                    >
                        <div className={classes.inner}>
                            {selected && (
                                <div className={classes.backdrop}></div>
                            )}

                            {cards.map((card) => (
                                <InspirationCard
                                    key={card.id}
                                    card={card}
                                    selected={card.id === selected?.id}
                                    onClose={handleClose}
                                    onOpen={handleOpenCard}
                                    layout={layout}
                                />
                            ))}
                        </div>
                    </div>
                </>
            )}

            {layout === 'mobile' && (
                <div className={classes.mobileInner} ref={boardRef}>
                    {cards.map((card) => (
                        <div key={card.id} className={classes.cardWrapMobile}>
                            <InspirationCard
                                card={card}
                                selected={card.id === selected?.id}
                                onClose={handleClose}
                                onOpen={handleOpenCard}
                                layout={layout}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
